import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import type { EmployeeUserResponse, ServiceModuleModel } from '@/api/model'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterSearch } from '@/components/common/filters/FilterSearch'
import { TableCard } from '@/components/common/table/TableCard'
import { useTimeTrackingSummaries } from '../hooks/useTimeTrackingSummaries'
import { TimeTrackingOverviewDataTable } from './TimeTrackingOverviewDataTable'
import { TimeTrackingExportDialog } from './TimeTrackingExportDialog'
import { WorkTimeQuotaDialog } from './WorkTimeQuotaDialog'
import { unwrap } from '@/utils/unwrap.ts'

interface Props {
  module: ServiceModuleModel
  /** Row click → employee view (view state lives in the page). */
  onRowClick?: (employee: EmployeeUserResponse) => void
}

/**
 * "Smart" overview of the time-tracking module: loads the employees + their metrics
 * (fan-out via `useTimeTrackingSummaries`), filters locally by name search and renders the
 * dumb `TimeTrackingOverviewDataTable`. Also holds the dialog state of the two
 * row buttons (work-time quota / export). Mirrors `AbsenceOverviewTable`.
 */
export function TimeTrackingOverviewTable({ module, onRowClick }: Props) {
  const { t } = useTranslation(['service-modules-timetracking', 'filters'])
  const [quotaEmployee, setQuotaEmployee] = useState<EmployeeUserResponse | null>(null)
  const [exportEmployee, setExportEmployee] = useState<EmployeeUserResponse | null>(null)
  const [search, setSearch] = useState<string | undefined>()

  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(module.id)
  const employees = useMemo(
    () => unwrap<EmployeeUserResponse[]>(employeesQuery.data) ?? [],
    [employeesQuery.data],
  )

  const filteredEmployees = useMemo(() => {
    const query = search?.trim().toLowerCase()
    if (!query) return employees
    return employees.filter((e) =>
      `${e.firstName ?? ''} ${e.lastName ?? ''}`.toLowerCase().includes(query),
    )
  }, [employees, search])

  const { summariesById } = useTimeTrackingSummaries(module.id, employees)

  return (
    <>
      <FilterRow>
        <FilterSearch
          search={search}
          onChange={setSearch}
          useDebounce={false}
          searchLabel={t('searchbar.searchEmployees', { ns: 'filters' })}
        />
      </FilterRow>

      <TableCard>
        <div className="flex-1 overflow-auto scrollbar-custom relative">
          <TimeTrackingOverviewDataTable
            employees={filteredEmployees}
            summariesById={summariesById}
            isLoading={employeesQuery.isLoading}
            isError={employeesQuery.isError}
            onRowClick={onRowClick}
            onShowQuota={setQuotaEmployee}
            onExport={setExportEmployee}
          />
        </div>
      </TableCard>

      {quotaEmployee && (
        <WorkTimeQuotaDialog
          open
          onOpenChange={(open) => !open && setQuotaEmployee(null)}
          serviceModuleId={module.id}
          employee={quotaEmployee}
        />
      )}

      <TimeTrackingExportDialog
        open={!!exportEmployee}
        onOpenChange={(open) => !open && setExportEmployee(null)}
        serviceModuleId={module.id}
        employee={exportEmployee ?? undefined}
      />
    </>
  )
}
